import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TextInput } from '../Onboarding.styled';
import OnboardingChip from './OnboardingChip';
import {
  ErrorText,
  Grid,
  Label,
  OtherInputRow,
  Wrapper,
} from './ChipPicker.styled';

export const OTHER_OPTION_ID = 'other';

export interface ChipOption<T extends string = string> {
  id: T;
  label: string;
}

export interface ChipPickerProps<T extends string> {
  /** Optional small uppercase label rendered above the grid. */
  label?: string;
  /** Available options. */
  options: ReadonlyArray<ChipOption<T>>;
  /** Currently selected option(s). */
  value: ReadonlyArray<T>;
  /** Number of columns in the chip grid. Defaults to 2. */
  columns?: 1 | 2 | 3;
  /** When true, allow multiple selection. Defaults to true. */
  multiple?: boolean;
  /** When true, append an "Other" chip that reveals a free-text input when selected. */
  withOther?: boolean;
  /** Free-text answer paired with "Other". */
  otherValue?: string;
  /** Called whenever the selection changes. */
  onChange: (next: T[]) => void;
  /** Called when the free-text "Other" answer changes. */
  onOtherChange?: (next: string) => void;
  /** Optional inline error rendered below the grid. */
  error?: string;
  /** Optional inline error rendered below the "Other" free-text input. */
  otherError?: string;
  /** Test ID prefix — child chips append `__chip-{id}`. */
  dataTestId?: string;
}

/**
 * Multi/single-select picker built from `OnboardingChip`. Optionally appends
 * an "Other" chip that reveals a free-text input.
 */
export function ChipPicker<T extends string>({
  label,
  options,
  value,
  columns = 2,
  multiple = true,
  withOther = false,
  otherValue = '',
  onChange,
  onOtherChange,
  error,
  otherError,
  dataTestId = 'chip-picker',
}: Readonly<ChipPickerProps<T>>) {
  const intl = useIntl();
  const selected = useMemo(() => new Set<string>(value), [value]);

  const toggle = useCallback(
    (id: T) => {
      if (multiple) {
        const next = new Set<string>(selected);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        onChange(Array.from(next) as T[]);
        return;
      }
      onChange(selected.has(id) ? [] : [id]);
    },
    [multiple, onChange, selected],
  );

  // The "Other" chip can come from one of two places: either it's already in
  // the options array (single-select like Manufacturing setup) or we append it
  // ourselves when `withOther` is set (multi-select tool stacks). Treat both
  // identically — we just need to know whether to show the free-text input.
  const otherInOptions = useMemo(
    () => options.some((opt) => opt.id === OTHER_OPTION_ID),
    [options],
  );
  const shouldAppendOtherChip = withOther && !otherInOptions;
  const otherSelected =
    (withOther || otherInOptions) && selected.has(OTHER_OPTION_ID);

  return (
    <Wrapper data-testid={dataTestId}>
      {label && <Label>{label}</Label>}
      <Grid
        $columns={columns}
        role={multiple ? 'group' : 'radiogroup'}
        aria-label={label}
      >
        {options.map((opt) => (
          <OnboardingChip
            key={opt.id}
            dataTestId={`${dataTestId}__chip-${opt.id}`}
            label={opt.label}
            selected={selected.has(opt.id)}
            onClick={() => toggle(opt.id)}
          />
        ))}
        {shouldAppendOtherChip && (
          <OnboardingChip
            dataTestId={`${dataTestId}__chip-${OTHER_OPTION_ID}`}
            label={intl.formatMessage({ id: 'onboarding.chip.other' })}
            selected={otherSelected}
            onClick={() => toggle(OTHER_OPTION_ID as T)}
          />
        )}
      </Grid>

      {otherSelected && onOtherChange && (
        <OtherInputRow>
          <TextInput
            data-testid={`${dataTestId}__other-input`}
            value={otherValue}
            onChange={(e) => onOtherChange(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'onboarding.chip.other.placeholder',
            })}
          />
          {otherError && (
            <ErrorText
              data-testid={`${dataTestId}__other-input__error`}
              role="alert"
            >
              {otherError}
            </ErrorText>
          )}
        </OtherInputRow>
      )}

      {error && (
        <ErrorText data-testid={`${dataTestId}__error`} role="alert">
          {error}
        </ErrorText>
      )}
    </Wrapper>
  );
}

export default ChipPicker;
